import { Group } from '@mantine/core';
import { type AdherenceLog, AdherenceLogEventType } from '../../../../api/events-client/events-client.type';
import { TASK_EVENT_TYPE_OPTIONS } from '../../../../api/workflows-client/workflows-client.type';
import i18n from '../../../../base-dictionary';
import { TableColumn } from '../../../../components/table/table';
import { secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { formatDatetimeToLocal } from '../../../data-management/components/data-management.helpers';
import { TruncatedText } from '../../risk-analysis/truncated-text';

/**
 * Gets the icon and color for the event type
 *
 * @param {AdherenceLogEventType} eventType - The type of adherence log event
 * @returns {Object} Icon name and color
 */
const getEventTypeIcon = (eventType: AdherenceLogEventType): { name: string; color: string } => {
  switch (eventType) {
    case AdherenceLogEventType.SKIPPED_STATUS:
      return { name: 'skip_next', color: skyDark };
    case AdherenceLogEventType.MISSED_EVENT:
      return { name: 'event_busy', color: skyDark };
    case AdherenceLogEventType.UNEXPECTED_EVENT:
      return { name: 'notification_important', color: skyDark };
    default:
      return { name: 'info', color: skyDark };
  }
};

/**
 * Formats the event type for display
 *
 * @param {AdherenceLogEventType} eventType - The type of adherence log event
 * @returns {string} Formatted event type string
 */
const formatEventType = (eventType: AdherenceLogEventType): string => {
  switch (eventType) {
    case AdherenceLogEventType.SKIPPED_STATUS:
      return 'Skipped Status';
    case AdherenceLogEventType.MISSED_EVENT:
      return 'Missed Event';
    case AdherenceLogEventType.UNEXPECTED_EVENT:
      return 'Unexpected Event';
    default:
      return eventType;
  }
};

/**
 * Formats the raw event for display
 *
 * @param {string} rawEvent - The raw event
 * @returns {string} Formatted raw event string
 */
const formatRawEvent = (rawEvent: string): string => {
  const event = TASK_EVENT_TYPE_OPTIONS.find((option) => option.value === rawEvent);
  if (event) {
    return event.label;
  }
  switch (rawEvent) {
    case 'status':
      return 'Status';
    default:
      return rawEvent;
  }
};

/**
 * Table columns configuration for the adherence logs table
 */
const AdherenceLogsTableColumns: TableColumn<AdherenceLog>[] = [
  {
    key: 'task' as keyof AdherenceLog,
    label: i18n.t('common.work_unit'),
    width: '10%',
    sortable: true,
    render: (_, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <a
          href={row.task.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
        >
          <Text color={secondaryBase}>{row.task.title}</Text> <Icon name="open_in_new" size={16} />
        </a>
      </div>
    ),
  },
  {
    key: 'event_type' as keyof AdherenceLog,
    label: 'Missed Event Type',
    width: '20%',
    sortable: true,
    render: (_, row) => {
      const eventType = row.event_type;
      const { name, color } = getEventTypeIcon(eventType);
      return (
        <Group gap="xs">
          <Icon name={name} color={color} size={16} />
          <Text>{formatEventType(eventType)}</Text>
        </Group>
      );
    },
  },
  {
    key: 'raw_event' as keyof AdherenceLog,
    label: `${i18n.t('common.work_unit')} Field`,
    width: '10%',
    sortable: true,
    render: (_, row) => <Text>{formatRawEvent(row.raw_event)}</Text>,
  },
  {
    key: 'message' as keyof AdherenceLog,
    label: 'Message',
    width: '25%',
    sortable: true,
    render: (_, row) => <TruncatedText text={row.message} />,
  },
  {
    key: 'changed_at' as keyof AdherenceLog,
    label: 'Date',
    width: '15%',
    sortable: true,
    render: (_, row) => <Text>{formatDatetimeToLocal(row.changed_at)}</Text>,
  },
  {
    key: 'changed_by' as keyof AdherenceLog,
    label: 'Changed By',
    width: '15%',
    sortable: true,
    render: (_, row) => <Text>{row.changed_by || '-'}</Text>,
  },
];

/**
 * Custom sort function for adherence logs
 *
 * @param {AdherenceLog[]} data - The data to sort
 * @param {keyof AdherenceLog} sortBy - The key to sort by
 * @param {string} direction - The direction to sort in
 * @returns {AdherenceLog[]} The sorted data
 */
const sortAdherenceLogs = (
  data: AdherenceLog[],
  sortBy: keyof AdherenceLog,
  direction: 'asc' | 'desc',
): AdherenceLog[] => {
  return [...data].sort((a, b) => {
    let comparison = 0;

    // Special handling for task field which is an object
    if (sortBy === 'task') {
      const aValue = a.task.title;
      const bValue = b.task.title;
      comparison = aValue.localeCompare(bValue);
    } else {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // Handle date strings by converting to Date objects
        if (sortBy === 'changed_at') {
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          comparison = aDate.getTime() - bDate.getTime();
        } else {
          comparison = aValue.localeCompare(bValue);
        }
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }
    }

    return direction === 'asc' ? comparison : -comparison;
  });
};

export { AdherenceLogsTableColumns, formatEventType, formatRawEvent, getEventTypeIcon, sortAdherenceLogs };
